/**
 * A well job's billing (api#1090): where its days stand between the field and
 * the bank, and the door that bills the customer-signed ones.
 *
 * EVERY FIGURE IS THE API'S (`GET /downhole/jobs/:id/billing`). Billed,
 * unbilled, outstanding and overdue are printed as returned; the browser adds
 * nothing up. Outstanding and overdue are the shared receivables rule (sent
 * invoices only), which is why a draft invoice counts as billed and not yet as
 * outstanding. An absent figure prints "—", never $0.
 *
 * WHICH DAYS CAN BE BILLED is the API's answer too: `signed_unbilled_ticket_ids`
 * (customer-approved and on no live invoice). The dialog offers exactly those,
 * all ticked, and sends the picked ones as `dh_ticket_ids`. It never sends
 * `dh_job_id`: that bills whatever is signed at the moment the request lands,
 * which may be a day the office never saw in this list.
 *
 * A refusal is shown as the API wrote it, with the days it names (`tickets[]`
 * on DAY_NOT_CUSTOMER_APPROVED, `waiting[]` on NO_SIGNED_DAYS_TO_BILL), each a
 * link to its ticket. The rules are not repeated here.
 *
 * READ: admin, office_staff, dispatcher (the route's roles). WRITE: admin and
 * office_staff, the one invoice door (canWriteDownholeInvoices). A field worker
 * is not shown the panel at all, so it never asks for a 403.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../api/client';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useGenerateInvoice } from '../../hooks/useDownhole';
import { useJobBilling, useJobDays } from '../../hooks/useDownholeJobs';
import { formatCalendarDate, formatInstantTenantDay } from '../../lib/calendar-date';
import { fmtCurrency, fmtQty } from '../../lib/display-labels';
import { DH_PRESENTED_INVOICE_STATUS_SKIN } from '../../lib/downhole-labels';
import { onlineOrSay } from '../../lib/downhole-offline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import type { DhJobBilling, DhJobDay, DhJobDetail } from '../../types/downhole';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhBadge, DhDialog, DhEmpty, DhField, DhPanel } from './DownholeBits';
import { TicketStatusBadge, apiErrorCode } from './ticketBits';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-60';
const DAY_FORMAT: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function BillingPanel({ job, tz, canInvoice }: {
  job: DhJobDetail;
  /** The tenant's zone, from the job read's meta: a due date reads as the tenant's day. */
  tz: string | null;
  canInvoice: boolean;
}) {
  const query = useJobBilling(job.id, true);
  const failure = queryLoadFailure(query);
  // A body without the `days` block is not a billing summary (an API from
  // before #1090 answers this path 404): it is shown as a failed read, never
  // as a page of zeros.
  const raw = query.data?.data ?? null;
  const billing = raw && typeof raw.days === 'object' && raw.days !== null && Array.isArray(raw.invoices) ? raw : null;
  const [invoicing, setInvoicing] = useState(false);
  const signed = billing?.days.signed_unbilled ?? 0;

  return (
    <DhPanel
      eyebrow="Billing"
      title="Invoicing"
      testId="well-billing"
      actions={
        canInvoice && billing ? (
          <button type="button" className={PRIMARY} onClick={() => setInvoicing(true)} disabled={signed === 0}>
            <Receipt className="size-4" aria-hidden="true" />
            Invoice approved days
          </button>
        ) : undefined
      }
    >
      {query.isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading billing" /></div>
      ) : failure || !billing ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this job's billing" />
      ) : (
        <BillingBody billing={billing} tz={tz} />
      )}
      {invoicing && billing && <InvoiceDaysDialog job={job} billing={billing} onClose={() => setInvoicing(false)} />}
    </DhPanel>
  );
}

function BillingBody({ billing, tz }: { billing: DhJobBilling; tz: string | null }) {
  const d = billing.days;
  const cur = billing.currency;
  return (
    <>
      <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="billing-summary">
        <Figure label="Billed" value={fmtCurrency(billing.billed_total, cur)} sub="On live invoices, drafts included" />
        <Figure
          label="Unbilled"
          value={fmtCurrency(d.signed_unbilled_total, cur)}
          sub={`${fmtQty(d.signed_unbilled)} signed ${d.signed_unbilled === 1 ? 'day' : 'days'} not yet invoiced`}
        />
        <Figure label="Outstanding" value={fmtCurrency(billing.receivable.outstanding, cur)} sub="Sent and not paid" />
        <Figure label="Overdue" value={fmtCurrency(billing.receivable.overdue, cur)} sub="Sent and past due" />
      </dl>
      <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text" data-testid="billing-days">
        {fmtQty(d.ticketed)} {d.ticketed === 1 ? 'day' : 'days'} ticketed: {fmtQty(d.draft)} draft, {fmtQty(d.office_approved)} waiting on
        the customer, {fmtQty(d.customer_rejected)} rejected, {fmtQty(d.customer_approved)} signed. {fmtQty(d.invoiced)} on an invoice.
      </p>
      {billing.invoices.length === 0 ? (
        <DhEmpty>No invoice has billed this job's days yet.</DhEmpty>
      ) : (
        <div className="overflow-x-auto border-t border-fieldiq-border">
          <table className="w-full min-w-[720px] text-left" aria-label="This job's invoices">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">Invoice</th>
                <th scope="col" className="px-4 py-3">Status</th>
                <th scope="col" className="px-4 py-3 text-right">Days</th>
                <th scope="col" className="px-4 py-3">Due</th>
                <th scope="col" className="px-4 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {billing.invoices.map((i) => (
                <tr key={i.id} className="border-t border-fieldiq-border">
                  <td className="whitespace-nowrap px-4 py-3">
                    <Link to={`/downhole/invoices/${i.id}`} className="font-mono text-sm font-extrabold text-fieldiq-accent-signal underline-offset-2 hover:underline">
                      {i.invoice_number || 'Open invoice'}
                    </Link>
                  </td>
                  <td className="px-4 py-3"><DhBadge map={DH_PRESENTED_INVOICE_STATUS_SKIN} value={i.status} /></td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{fmtQty(i.day_count)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{/* dh_invoices.due_date is a calendar `date` the route sends as 'YYYY-MM-DD';
                      formatInstantTenantDay passes a bare day through unchanged, as the
                      invoice list and detail screens do. */}
                  {formatInstantTenantDay(i.due_date, tz) ?? '—'}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{fmtCurrency(i.total, i.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Figure({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div>
      <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{label}</dt>
      <dd className="mt-0.5 font-mono text-lg font-bold text-white">{value}</dd>
      <dd className="text-xs text-fieldiq-muted">{sub}</dd>
    </div>
  );
}

interface NamedDay {
  id: string;
  ticket_number: string | null;
  work_date: string | null;
  status?: string | null;
}

interface Refusal {
  code: string | null;
  message: string;
  days: NamedDay[];
}

/** The days a refusal names, as the API sent them: `tickets[]` or `waiting[]`. */
export function refusalDays(error: unknown): NamedDay[] {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  const list = Array.isArray(data?.tickets) ? data!.tickets : Array.isArray(data?.waiting) ? data!.waiting : [];
  return (list as NamedDay[]).filter((t) => t && typeof t.id === 'string');
}

function InvoiceDaysDialog({ job, billing, onClose }: { job: DhJobDetail; billing: DhJobBilling; onClose: () => void }) {
  const navigate = useNavigate();
  const daysQuery = useJobDays(job.id, true);
  const generate = useGenerateInvoice();
  const offered = billing.days.signed_unbilled_ticket_ids;
  // The day each offered ticket belongs to, for its date and total. A ticket
  // the days read does not carry still shows (by number unknown), never dropped.
  const byTicket = useMemo(() => {
    const m = new Map<string, DhJobDay>();
    for (const d of daysQuery.data?.data ?? []) if (d.ticket_id) m.set(d.ticket_id, d);
    return m;
  }, [daysQuery.data]);
  const currency = daysQuery.data?.meta?.currency ?? billing.currency;

  const [picked, setPicked] = useState<string[]>(offered);
  const [due, setDue] = useState('');
  const [notes, setNotes] = useState('');
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const dueBad = due !== '' && !DAY.test(due);

  const submit = () => {
    setRefusal(null);
    if (!onlineOrSay((m) => setRefusal({ code: null, message: m, days: [] }))) return;
    generate.mutate(
      { dh_ticket_ids: picked, ...(due && { due_date: due }), ...(notes.trim() && { notes: notes.trim() }) },
      {
        onSuccess: (res) => {
          const inv = res.data;
          toast.success(inv?.invoice_number ? `Invoice ${inv.invoice_number} created` : 'Invoice created');
          onClose();
          if (inv?.id) navigate(`/downhole/invoices/${inv.id}`);
        },
        onError: (err) =>
          setRefusal({ code: apiErrorCode(err), message: getApiErrorMessage(err, 'The invoice was not created.'), days: refusalDays(err) }),
      },
    );
  };

  return (
    <DhDialog
      open
      wide
      title={`Invoice approved days: ${job.job_number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose} disabled={generate.isPending}>Cancel</button>
          <button type="button" className={PRIMARY} onClick={submit} disabled={picked.length === 0 || dueBad || generate.isPending}>
            {generate.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Create invoice
          </button>
        </>
      }
    >
      <div className="space-y-4" data-testid="invoice-days">
        <p className="text-sm text-fieldiq-text">
          These are the days the customer has signed that are not on an invoice yet. The invoice bills each day at the prices on its signed
          ticket; today&apos;s rates are only compared, and any difference goes in the invoice notes.
        </p>
        {offered.length === 0 ? (
          <DhEmpty>No signed day is waiting to be billed.</DhEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
            {offered.map((id) => {
              const d = byTicket.get(id);
              const label = d ? `${formatCalendarDate(d.work_date, DAY_FORMAT) ?? d.work_date}, ${d.ticket_number ?? 'ticket'}` : 'Signed day ticket';
              return (
                <li key={id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <label className="flex min-w-0 items-center gap-3 text-sm text-fieldiq-text">
                    <input type="checkbox" checked={picked.includes(id)} onChange={() => toggle(id)} aria-label={label} />
                    <span className="truncate">
                      <span className="font-bold text-white">{d ? formatCalendarDate(d.work_date, DAY_FORMAT) ?? d.work_date : '—'}</span>{' '}
                      <Link to={`/downhole/tickets/${id}`} className="font-mono font-bold text-fieldiq-accent-signal underline-offset-2 hover:underline">
                        {d?.ticket_number ?? 'Open ticket'}
                      </Link>
                    </span>
                  </label>
                  <span className="whitespace-nowrap font-mono text-sm text-fieldiq-text">{d ? fmtCurrency(d.ticket_total, currency) : '—'}</span>
                </li>
              );
            })}
          </ul>
        )}
        {billing.days.office_approved > 0 && (
          <p className="text-sm text-fieldiq-muted">
            {fmtQty(billing.days.office_approved)} more {billing.days.office_approved === 1 ? 'day is' : 'days are'} waiting on the customer and
            cannot be billed yet.
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <DhField label="Due date" hint="Leave empty to use the customer's payment terms.">
            <input type="date" className={DH_DATE_INPUT} value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due date" />
          </DhField>
          <DhField label="Notes">
            <input className={DH_INPUT} value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} aria-label="Notes" />
          </DhField>
        </div>
        {refusal && (
          <div role="alert" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
            <p className="font-bold">{refusal.message}</p>
            {refusal.code && <p className="mt-1 font-mono text-xs text-fieldiq-muted">{refusal.code}</p>}
            {refusal.days.length > 0 && (
              <ul className="mt-2 space-y-1">
                {refusal.days.map((t) => (
                  <li key={t.id} className="flex flex-wrap items-center gap-2">
                    <Link to={`/downhole/tickets/${t.id}`} className="font-mono font-bold text-fieldiq-accent-signal underline-offset-2 hover:underline">
                      {t.ticket_number ?? 'Open ticket'}
                    </Link>
                    <span>{formatCalendarDate(t.work_date) ?? '—'}</span>
                    {t.status && <TicketStatusBadge status={t.status} />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </DhDialog>
  );
}
